import { useAppSelector } from '@store';
import { Controller } from 'react-hook-form';
import {
  Autocomplete,
  InputNumber,
  InputSize,
  InputText,
  Select,
  SelectSize,
  Textarea,
} from '@ui/src';
import AdditionalInfoBlock from '../additional-info-block';
import { ProfessionalInformationTypes } from './types';
import RegulatedCompanyRow from '../../../corporate/aml/parts/row';
import { CustomerStatus } from '../../../../hoc/customers/types';

import cn from '../aml.module.css';

const ProfessionalInformation = ({
  organizationControl,
  organizationErrors,
  organizationClearErrors,
  control,
  errors,
  clearErrors,
  countries,
  status,
  organizationFields,
}: ProfessionalInformationTypes) => {
  const { clientsActivities } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const employmentActivity = clientsActivities?.find(
    (item) => item.name === 'Employment',
  );
  const otherActivity = clientsActivities?.find(
    (item) => item.name === 'Other',
  );
  const businessOwnerActivity = clientsActivities?.find(
    (item) => item.name === 'Business Owner',
  );

  const employmentContent = (
    <>
      <RegulatedCompanyRow title="Name of employer">
        <Controller
          control={organizationControl}
          name="nameOfEmployer"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="nameOfEmployer"
              placeholder="Enter name of employer"
              value={value}
              onChange={(e) => {
                onChange(e);

                if (organizationClearErrors) {
                  organizationClearErrors(['nameOfEmployer']);
                }
              }}
              error={organizationErrors?.nameOfEmployer?.message}
              size={InputSize.SMALL}
              className="borderless"
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        />
      </RegulatedCompanyRow>
      <RegulatedCompanyRow title="Nature of business activity">
        <Controller
          control={control}
          name="natureOfBusinessActivity"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <InputText
              name={name}
              placeholder="Enter description..."
              value={value}
              onChange={(e) => {
                onChange(e);
                clearErrors(['natureOfBusinessActivity']);
              }}
              size={InputSize.SMALL}
              error={errors?.natureOfBusinessActivity?.message}
              className="borderless"
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        />
      </RegulatedCompanyRow>
      <RegulatedCompanyRow title="Position">
        <Controller
          control={organizationControl}
          name="employeePosition"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="employeePosition"
              placeholder="Enter position"
              value={value}
              onChange={(e) => {
                onChange(e);

                if (organizationClearErrors) {
                  organizationClearErrors(['employeePosition']);
                }
              }}
              size={InputSize.SMALL}
              error={organizationErrors?.employeePosition?.message}
              className="borderless"
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        />
      </RegulatedCompanyRow>
      <RegulatedCompanyRow title="Country of Employment">
        <Controller
          control={organizationControl}
          name="countryOfEmployerId"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              name="countryOfEmployerId"
              placeholder="Select country..."
              list={countries || []}
              value={value}
              className={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              error={organizationErrors?.countryOfEmployerId?.message}
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange(e);

                if (organizationClearErrors) {
                  organizationClearErrors(['countryOfEmployerId']);
                }
              }}
              borderless
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              disabled={status === CustomerStatus.CLOSED}
              fullWidth
            />
          )}
        />
      </RegulatedCompanyRow>
    </>
  );

  return (
    <AdditionalInfoBlock title="Professional information">
      <RegulatedCompanyRow title="Activity of the client">
        <Controller
          control={organizationControl}
          name="clientActivityId"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <Select
              name="clientActivityId"
              placeholder="Select an option"
              list={clientsActivities}
              value={value}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange(e);

                if (organizationClearErrors) {
                  organizationClearErrors(['clientActivityId']);
                }
              }}
              error={organizationErrors?.clientActivityId?.message}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              disabled={status === CustomerStatus.CLOSED}
              fullWidth
              borderless
            />
          )}
        />
      </RegulatedCompanyRow>
      {employmentActivity?.value === organizationFields?.clientActivityId &&
        employmentContent}
      {clientsActivities?.find((item) => item.name === 'Self-employed')
        ?.value === organizationFields?.clientActivityId && (
        <RegulatedCompanyRow title="Nature of business activity">
          <Controller
            control={control}
            name="natureOfBusinessActivity"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <InputText
                name={name}
                placeholder="Enter description..."
                value={value}
                onChange={(e) => {
                  onChange(e);
                  clearErrors(['natureOfBusinessActivity']);
                }}
                size={InputSize.SMALL}
                error={errors?.natureOfBusinessActivity?.message}
                className="borderless"
                tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                disabled={status === CustomerStatus.CLOSED}
              />
            )}
          />
        </RegulatedCompanyRow>
      )}
      {otherActivity?.value === organizationFields?.clientActivityId && (
        <RegulatedCompanyRow title="Description of activity" textarea>
          <Controller
            control={organizationControl}
            name="typeOfActivityDescription"
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <Textarea
                name="typeOfActivityDescription"
                placeholder="Enter description..."
                onChange={(e) => {
                  onChange(e);

                  if (organizationClearErrors) {
                    organizationClearErrors(['typeOfActivityDescription']);
                  }
                }}
                value={value}
                className={`borderless ${cn.stepOneTextarea} ${cn.pepTextarea}`}
                error={organizationErrors?.typeOfActivityDescription?.message}
                tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                disabled={status === CustomerStatus.CLOSED}
              />
            )}
          />
        </RegulatedCompanyRow>
      )}
      {businessOwnerActivity?.value ===
        organizationFields?.clientActivityId && (
        <>
          <RegulatedCompanyRow title="Name of the Company">
            <Controller
              control={control}
              name="nameOfCompany"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Enter name..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['nameOfCompany']);
                  }}
                  size={InputSize.SMALL}
                  error={errors?.nameOfCompany?.message}
                  className="borderless"
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  disabled={status === CustomerStatus.CLOSED}
                />
              )}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Ownership in %">
            <Controller
              control={control}
              name="ownership"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputNumber
                  name={name}
                  placeholder="Enter percent..."
                  value={value}
                  onChange={(e) => {
                    let ownershipValue = e.target.value;

                    if (Number(e.target.value) > 100) {
                      ownershipValue = '100';
                    } else if (Number(e.target.value) < 0) {
                      ownershipValue = '0';
                    }

                    onChange(ownershipValue);
                    clearErrors(['ownership']);
                  }}
                  size={InputSize.SMALL}
                  error={errors?.ownership?.message}
                  className="borderless"
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  disabled={status === CustomerStatus.CLOSED}
                />
              )}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Country of Employment">
            <Controller
              control={organizationControl}
              name="countryOfEmployerId"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  name="countryOfEmployerId"
                  placeholder="Select country..."
                  list={countries || []}
                  value={value}
                  selectWrapperClassName={
                    status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
                  }
                  error={organizationErrors?.countryOfEmployerId?.message}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);

                    if (organizationClearErrors) {
                      organizationClearErrors(['countryOfEmployerId']);
                    }
                  }}
                  borderless
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  disabled={status === CustomerStatus.CLOSED}
                  fullWidth
                />
              )}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Nature of business activity">
            <Controller
              control={control}
              name="natureOfBusinessActivity"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Enter description..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['natureOfBusinessActivity']);
                  }}
                  size={InputSize.SMALL}
                  error={errors?.natureOfBusinessActivity?.message}
                  className="borderless"
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  disabled={status === CustomerStatus.CLOSED}
                />
              )}
            />
          </RegulatedCompanyRow>
        </>
      )}
    </AdditionalInfoBlock>
  );
};

export default ProfessionalInformation;
